import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface PersonOfInterest {
  id: string;
  name: string;
}

// Interfaces for both components
interface TrailerCaravan {
  id?: string | number;
  vehicle_type?: string;
  vehicleType?: string;
  registration_number?: string;
  registrationNumber?: string;
  make?: string;
  model?: string;
  year?: string;
  cover_type?: string;
  coverType?: string;
  insured_value?: string | number;
  insuredValue?: string | number;
  owner?: string;
  use_retail_value?: boolean;
  useRetailValue?: boolean;
  premium?: string | number;
  excess?: string | number;
  no_claims_bonus?: string;
  noClaimsBonus?: string;
  isEditing?: boolean;
  isTemp?: boolean;
  quote_id?: string | number;
  quoteId?: string | number;
  client_id?: string | number;
  clientId?: string | number;
}

type SaveStatus = 'idle' | 'loading' | 'success' | 'error' | 'warning' | 'info';

interface TrailerCaravanModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSave: (data: TrailerCaravan) => void;
  clientId: number;
  setSaveStatus?: (status: SaveStatus) => void;
  setSaveMessage?: (message: string) => void;
  trailerCaravan?: TrailerCaravan;
  mode?: 'create' | 'edit' | 'view';
  quoteId?: number | string;
}

interface EditableTrailerCaravanCardProps {
  trailerCaravan: TrailerCaravan;
  index: number;
  onSave: (index: number, updatedData: any) => void;
  onCancel: (index: number) => void;
  onEdit: (index: number) => void;
  cardNumber?: number;
  cardTitle?: string;
  showCardNumber?: boolean;
  clientId?: number;
}

interface Client {
  id: string;
  name: string;
  isCurrentClient?: boolean;
}

// Helper function to map API data to form data
const mapApiDataToForm = (apiData: any): TrailerCaravan => {
  if (!apiData) {
    return {
      vehicleType: 'Trailer',
      registrationNumber: '',
      make: '',
      model: '',
      year: '',
      coverType: '',
      insuredValue: '',
      owner: '',
      useRetailValue: false,
      premium: '',
      excess: '',
      noClaimsBonus: '',
      quote_id: undefined,
      quoteId: undefined,
      client_id: undefined,
      clientId: undefined,
    };
  }
  
  console.log('🔍 Mapping API data:', apiData);
  
  // Convert year to string if it exists
  const yearValue = apiData.year !== undefined && apiData.year !== null 
    ? String(apiData.year) 
    : '';
  
  return {
    id: apiData.id,
    // Handle both snake_case and camelCase
    vehicleType: apiData.vehicleType || apiData.vehicle_type || 'Trailer',
    registrationNumber: apiData.registrationNumber || apiData.registration_number || '',
    make: apiData.make || '',
    model: apiData.model || '',
    year: yearValue, // ⭐ Always string
    coverType: apiData.coverType || apiData.cover_type || '',
    insuredValue: apiData.insuredValue || apiData.insured_value || '',
    owner: apiData.owner || '',
    useRetailValue: apiData.useRetailValue !== undefined ? apiData.useRetailValue : (apiData.use_retail_value !== undefined ? apiData.use_retail_value : false),  // FIX THIS LINE
    premium: apiData.premium || '',
    excess: apiData.excess || '',
    noClaimsBonus: apiData.noClaimsBonus || apiData.no_claims_bonus || '',
    quote_id: apiData.quote_id || apiData.quoteId,
    quoteId: apiData.quoteId || apiData.quote_id,
    client_id: apiData.client_id || apiData.clientId,
    clientId: apiData.clientId || apiData.client_id,
    isTemp: apiData.isTemp,
  };
};

const API_URL = process.env.REACT_APP_API_URL;

// Currency formatting helper functions
const formatCurrencyInput = (value: string | number): string => {
  if (value === undefined || value === null || value === '') return '';
  
  // Convert to string
  const stringValue = typeof value === 'number' ? value.toString() : String(value);
  
  // Remove any existing formatting
  const numericValue = stringValue.replace(/[^\d.]/g, '');
  
  // If empty after cleaning, return empty
  if (!numericValue) return '';
  
  // Split into whole and decimal parts
  const parts = numericValue.split('.');
  let wholePart = parts[0] || '';
  let decimalPart = parts[1] || '';
  
  // Limit decimal to 2 digits
  if (decimalPart.length > 2) {
    decimalPart = decimalPart.substring(0, 2);
  }
  
  // Format the whole number part with commas
  if (wholePart) {
    // Remove leading zeros
    wholePart = wholePart.replace(/^0+/, '') || '0';
    wholePart = parseInt(wholePart, 10).toLocaleString('en-US');
  }
  
  // Combine parts
  return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
};

const parseCurrencyInput = (value: string): number => {
  if (!value) return 0;
  return parseFloat(value.replace(/,/g, ''));
};

// Format currency for display
const formatCurrencyForDisplay = (value: number | string): string => {
  if (!value && value !== 0) return '';
  
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  
  if (isNaN(numValue)) return value.toString();
  
  return numValue.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Main Modal Component
export const TrailerCaravanModal: React.FC<TrailerCaravanModalProps> = ({ 
  isVisible, 
  onClose, 
  onSave, 
  clientId, 
  setSaveStatus, 
  setSaveMessage,
  trailerCaravan,
  mode = 'create',
  quoteId 
}) => {
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const ensureString = (value: string | number | undefined): string => {
    if (value === undefined || value === null) return '';
    return typeof value === 'number' ? value.toString() : String(value);
  };

  // Initialize form data based on mode
  const getInitialFormData = (): TrailerCaravan => {
  console.log('🔄 Getting initial form data for mode:', mode);
  console.log('📦 Received trailerCaravan data:', trailerCaravan);
  
  if (isEditMode || isViewMode) {
    const mappedData = mapApiDataToForm(trailerCaravan);
    console.log('✅ Mapped form data:', mappedData);
    return mappedData;
  }
  
  // For create mode, use empty defaults
  return {
    vehicleType: 'Trailer',
    registrationNumber: '',
    make: '',
    model: '',
    year: '', // ⭐ Make sure this is an empty string, not undefined
    coverType: '',
    insuredValue: '',
    owner: '',
    useRetailValue: false,
    premium: '',
    excess: '',
    noClaimsBonus: '',
    quote_id: undefined,
    quoteId: undefined,
    client_id: clientId,
    clientId: clientId
  };
};

  const [formData, setFormData] = useState<TrailerCaravan>(getInitialFormData);
  const [currencyDisplay, setCurrencyDisplay] = useState(() => {
  // Use the mapping function to get proper data
  const initialData = getInitialFormData();
  
  const insuredValue = initialData.insuredValue ? 
    (typeof initialData.insuredValue === 'string' ? 
      parseFloat(initialData.insuredValue) : 
      Number(initialData.insuredValue)) : 0;
  
  const premium = initialData.premium ? 
    (typeof initialData.premium === 'string' ? 
      parseFloat(initialData.premium) : 
      Number(initialData.premium)) : 0;
  
  const excess = initialData.excess ? 
    (typeof initialData.excess === 'string' ? 
      parseFloat(initialData.excess) : 
      Number(initialData.excess)) : 0;
  
  return {
    insuredValue: insuredValue > 0 ? formatCurrencyForDisplay(insuredValue) : '',
    premium: premium > 0 ? formatCurrencyForDisplay(premium) : '',
    excess: excess > 0 ? formatCurrencyForDisplay(excess) : '',
  };
});

  const [errors, setErrors] = useState({
    vehicleType: '',
    registrationNumber: '',
    make: '',
    model: '',
    year: '',
    coverType: '',
    insuredValue: '',
    owner: '',
    premium: '',
    excess: '',
    noClaimsBonus: '',
  });

  const [clients, setClients] = useState<Client[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  const [localSaveStatus, setLocalSaveStatus] = useState<SaveStatus>('idle');
  const [localSaveMessage, setLocalSaveMessage] = useState('');

  // Use provided setSaveStatus/setSaveMessage or local state
  const actualSetSaveStatus = setSaveStatus || setLocalSaveStatus;
  const actualSetSaveMessage = setSaveMessage || setLocalSaveMessage;

  // Reset form when mode or trailerCaravan changes
  useEffect(() => {
    console.log('🔄 useEffect triggered - isVisible:', isVisible, 'mode:', mode);
    const initialData = getInitialFormData();
    console.log('📝 Setting form data to:', initialData);
    
    setFormData(initialData);
    
    // Ensure currency values are properly handled
    const insuredValueNum = initialData.insuredValue ? 
      (typeof initialData.insuredValue === 'string' ? 
        parseFloat(initialData.insuredValue) : 
        Number(initialData.insuredValue)) : 0;
    
    const premiumNum = initialData.premium ? 
      (typeof initialData.premium === 'string' ? 
        parseFloat(initialData.premium) : 
        Number(initialData.premium)) : 0;
    
    const excessNum = initialData.excess ? 
      (typeof initialData.excess === 'string' ? 
        parseFloat(initialData.excess) : 
        Number(initialData.excess)) : 0;
    
    console.log('💰 Currency values:', {
      insuredValue: insuredValueNum,
      premium: premiumNum,
      excess: excessNum
    });
    
    setCurrencyDisplay({
      insuredValue: insuredValueNum > 0 ? formatCurrencyForDisplay(insuredValueNum) : '',
      premium: premiumNum > 0 ? formatCurrencyForDisplay(premiumNum) : '',
      excess: excessNum > 0 ? formatCurrencyForDisplay(excessNum) : '',
    });
  }, [trailerCaravan, mode, isVisible]);

  useEffect(() => {
    const fetchClientsAndPOIs = async () => {
      setIsLoadingClients(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        // 1. Fetch current client details
        const clientResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/clients/${clientId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!clientResponse.ok) {
          const errorText = await clientResponse.text();
          console.error('Fetch current client failed:', clientResponse.status, errorText);
          throw new Error(`Failed to fetch current client: ${clientResponse.status}`);
        }

        const clientData = await clientResponse.json();
        const currentClient = {
          id: clientData.id?.toString() || clientId.toString(),
          name: clientData.name || `${clientData.first_name || ''} ${clientData.last_name || ''}`.trim() || `Client ${clientId}`,
          isCurrentClient: true
        };

        // 2. Fetch persons of interest for this client USING QUERY PARAMETER
        let personsOfInterest: any[] = [];
        try {
          const poiResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/persons-of-interest?clientId=${clientId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (poiResponse.ok) {
            const poiData = await poiResponse.json();
            console.log('Persons of interest data:', poiData);
            
            personsOfInterest = (poiData.data || poiData || []).map((poi: any) => ({
              id: `poi-${poi.id}`,
              // Combine name and surname if both exist
              name: `${poi.name || ''} ${poi.surname || ''}`.trim() || poi.name || poi.surname || `Person of Interest ${poi.id}`,
            })).filter((poi: any) => poi.id && poi.name);
            
            console.log('Processed persons of interest:', personsOfInterest);
          } else {
            console.warn('Persons of interest fetch failed with status:', poiResponse.status);
          }
        } catch (poiError) {
          console.warn('Could not fetch persons of interest:', poiError);
          // Continue without POIs if this fails
        }

        // 3. Combine current client and persons of interest
        const allOwners: Client[] = [
          currentClient,
          ...personsOfInterest
        ].filter(item => item.id && item.name);

        console.log('Available owners:', allOwners);
        setClients(allOwners);

        // 4. Auto-select current client in create mode if no owner is selected
        if (isCreateMode && !formData.owner && allOwners.length > 0) {
          setFormData(prev => ({
            ...prev,
            owner: currentClient.id
          }));
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        actualSetSaveStatus('error');
        actualSetSaveMessage(`Failed to load owner data: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setClients([]);
      } finally {
        setIsLoadingClients(false);
      }
    };

    if (isVisible) {
      fetchClientsAndPOIs();
    }
  }, [isVisible, clientId, isCreateMode, actualSetSaveStatus, actualSetSaveMessage, formData.owner]);

  // Debug useEffect to log current state
useEffect(() => {
  if (isEditMode || isViewMode) {
    console.log('🔍 DEBUG - Modal form state:');
    console.log('Form Data:', formData);
    console.log('Currency Display:', currencyDisplay);
    console.log('Original trailerCaravan prop:', trailerCaravan);
  }
}, [formData, currencyDisplay, isEditMode, isViewMode, trailerCaravan]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
  if (isViewMode) return; // Don't allow changes in view mode
  
  const { name, value, type, checked } = e.target as HTMLInputElement;
  
  if (name === 'insuredValue' || name === 'premium' || name === 'excess') {
    // Treat value as string since it comes from an input
    const stringValue = value; // This is always a string from HTML input
    const formattedValue = formatCurrencyInput(stringValue);
    const numericValue = parseCurrencyInput(stringValue);
    
    setFormData(prev => ({
      ...prev,
      [name]: numericValue ? numericValue.toString() : '',
    }));
    
    setCurrencyDisplay(prev => ({
      ...prev,
      [name]: formattedValue,
    }));
  } else {
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }
  
  setErrors((prev) => ({ ...prev, [name]: '' }));
};

  const handleCurrencyBlur = (field: 'insuredValue' | 'premium' | 'excess') => {
    const currentValue = currencyDisplay[field];
    if (currentValue) {
      const numericValue = parseCurrencyInput(currentValue);
      if (numericValue > 0) {
        const formatted = formatCurrencyInput(numericValue.toFixed(2));
        setCurrencyDisplay(prev => ({ ...prev, [field]: formatted }));
      }
    }
  };

  const validateForm = () => {
  const currentYear = new Date().getFullYear();
  const minYear = 1900;
  const maxYear = currentYear + 1;
  
  let yearError = '';
  if (formData.year) { // Only validate if year is provided
    const yearNum = parseInt(formData.year);
    if (isNaN(yearNum) || yearNum < minYear || yearNum > maxYear) {
      yearError = `Year must be between ${minYear} and ${maxYear}`;
    }
  }

  const newErrors = {
    vehicleType: '', // Not required for editing
    registrationNumber: '', // Not required for editing
    make: '', // Not required for editing
    model: '', // Not required for editing
    year: yearError, // Only validate format if provided
    coverType: '', // Not required for editing
    insuredValue: '', // Not required for editing
    owner: '', // Not required for editing
    premium: '', // Not required for editing
    excess: '', // Not required for editing
    noClaimsBonus: '', // Not required for editing
  };
  
  setErrors(newErrors);
  
  // Only return false if there are format errors (like invalid year)
  return !Object.values(newErrors).some(error => error !== '');
};

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (isViewMode) return;
  
  console.log('🚐 DEBUG TrailerCaravanModal - Starting save process');
  console.log('🔑 quoteId prop value:', quoteId);
  console.log('🔑 formData.quote_id value:', formData.quote_id);
  console.log('🔑 formData.quoteId value:', formData.quoteId);
  
  // Prepare the trailer data object
  const trailerData = {
    vehicleType: formData.vehicleType || 'Trailer',
    registrationNumber: formData.registrationNumber || '',
    make: formData.make || '',
    model: formData.model || '',
    year: formData.year ? String(formData.year) : undefined,
    coverType: formData.coverType || '',
    insuredValue: formData.insuredValue ? parseFloat(String(formData.insuredValue)) || 0 : 0,
    client_id: clientId,
    useRetailValue: formData.useRetailValue || false,
    premium: formData.premium ? parseFloat(String(formData.premium)) || 0 : 0,
    excess: formData.excess ? parseFloat(String(formData.excess)) || 0 : 0,
    noClaimsBonus: formData.noClaimsBonus || '',
    quote_id: quoteId ? parseInt(String(quoteId)) : (formData.quote_id || formData.quoteId)
  };

  console.log('📝 DEBUG TrailerCaravanModal - Final trailer data to save:', trailerData);

  // If editing (has real ID), call the API directly
  if (formData.id && !formData.id.toString().startsWith('temp-')) {
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        vehicle_type: trailerData.vehicleType,
        registration_number: trailerData.registrationNumber,
        make: trailerData.make,
        model: trailerData.model,
        year: trailerData.year ? parseInt(trailerData.year) : null,
        cover_type: trailerData.coverType,
        insured_value: trailerData.insuredValue,
        use_retail_value: trailerData.useRetailValue === true,
        premium: trailerData.premium,
        excess: trailerData.excess,
        no_claims_bonus: trailerData.noClaimsBonus,
        quote_id: trailerData.quote_id
      };
      
      console.log('🔴🔴🔴 DIRECT PUT UPDATE:', {
        url: `${API_URL}/api/trailer-caravans/${formData.id}`,
        data: updateData
      });
      
      const response = await axios.put(
        `${API_URL}/api/trailer-caravans/${formData.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('✅ Direct update successful:', response.data);
      
      // Also call onSave to update parent state
      if (onSave && typeof onSave === 'function') {
        onSave(trailerData);
      }
      
      if (setSaveStatus && setSaveMessage) {
        setSaveStatus('success');
        setSaveMessage('Trailer/Caravan updated successfully!');
      }
      
      onClose();
      return;
    } catch (error: any) {
      console.error('❌ Direct update failed:', error);
      // Fall through to onSave if direct update fails
    }
  }

  // Call the parent's onSave function (for create or fallback)
  if (onSave && typeof onSave === 'function') {
    console.log('✅ DEBUG: Calling onSave with data');
    onSave(trailerData);
    
    if (setSaveStatus && setSaveMessage) {
      setSaveStatus('success');
      setSaveMessage('Trailer/Caravan added successfully!');
    }
    
    onClose();
  } else {
    console.error('❌ DEBUG: onSave is not a function or is undefined!');
    alert('Error: Cannot save trailer/caravan. Please try again.');
  }
};

  const handleClose = () => {
    setFormData(getInitialFormData());
    setCurrencyDisplay({
      insuredValue: '',
      premium: '',
      excess: '',
    });
    setErrors({
      vehicleType: '',
      registrationNumber: '',
      make: '',
      model: '',
      year: '',
      coverType: '',
      insuredValue: '',
      owner: '',
      premium: '',
      excess: '',
      noClaimsBonus: '',
    });
    actualSetSaveStatus('idle');
    actualSetSaveMessage('');
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
      <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">
          {isViewMode ? 'View Trailer/Caravan Details' : 
           isEditMode ? 'Edit Trailer/Caravan Details' : 'Add Trailer/Caravan'}
        </h3>

        {localSaveStatus === 'success' && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
            {localSaveMessage}
          </div>
        )}
        {localSaveStatus === 'error' && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            {localSaveMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 mb-6">
            <div className="grid grid-cols-3 gap-4">
                <div>
                <label htmlFor="vehicleType" className="block text-gray-700 font-bold mb-2 text-sm">
                    Vehicle Type
                </label>
                <select
                    id="vehicleType"
                    name="vehicleType"
                    value={formData.vehicleType}
                    onChange={handleChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                >
                    <option value="Trailer">Trailer</option>
                    <option value="Caravan">Caravan</option>
                </select>
                {errors.vehicleType && <p className="text-red-500 text-xs mt-1">{errors.vehicleType}</p>}
                </div>
                <div>
                <label htmlFor="registrationNumber" className="block text-gray-700 font-bold mb-2 text-sm">
                    Registration Number
                </label>
                <input
                    type="text"
                    id="registrationNumber"
                    name="registrationNumber"
                    value={formData.registrationNumber}
                    onChange={handleChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    placeholder="Enter registration number"
                    disabled={isViewMode}
                />
                {errors.registrationNumber && <p className="text-red-500 text-xs mt-1">{errors.registrationNumber}</p>}
                </div>
                <div>
                <label htmlFor="make" className="block text-gray-700 font-bold mb-2 text-sm">
                    Trailer/Caravan Make
                </label>
                <input
                    type="text"
                    id="make"
                    name="make"
                    value={formData.make}
                    onChange={handleChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    placeholder="Enter make"
                    disabled={isViewMode}
                />
                {errors.make && <p className="text-red-500 text-xs mt-1">{errors.make}</p>}
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="model" className="block text-gray-700 font-bold mb-2 text-sm">
                Trailer/Caravan Model
              </label>
              <input
                type="text"
                id="model"
                name="model"
                value={formData.model}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                placeholder="Enter model"
                disabled={isViewMode}
              />
              {errors.model && <p className="text-red-500 text-xs mt-1">{errors.model}</p>}
            </div>

            <div>
              <label htmlFor="year" className="block text-gray-700 font-bold mb-2 text-sm">
                Year
              </label>
              <input
                type="number"
                id="year"
                name="year"
                value={formData.year}
                onChange={handleChange}
                min="1900"
                max={new Date().getFullYear() + 1}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder={`Enter year (1900-${new Date().getFullYear() + 1})`}
                disabled={isViewMode}
              />
              {errors.year && <p className="text-red-500 text-xs mt-1">{errors.year}</p>}
            </div>

                <div>
                <label htmlFor="coverType" className="block text-gray-700 font-bold mb-2 text-sm">
                    Cover Type
                </label>
                <select
                    id="coverType"
                    name="coverType"
                    value={formData.coverType}
                    onChange={handleChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                >
                    <option value="">Select cover type</option>
                    <option value="Comprehensive">Comprehensive</option>
                    <option value="Third Party">Compulsary Third Party</option>
                    <option value="Fire and Theft">Third Party Fire and Theft</option>
                    <option value="Property Damage">Third Party Property Damage</option>
                </select>
                {errors.coverType && <p className="text-red-500 text-xs mt-1">{errors.coverType}</p>}
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="insuredValue" className="block text-gray-700 font-bold mb-2 text-sm">
                  Insured Value <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">R</span>
                  </div>
                  <input
                    type="text"
                    id="insuredValue"
                    name="insuredValue"
                    value={currencyDisplay.insuredValue}
                    onChange={handleChange}
                    onBlur={() => handleCurrencyBlur('insuredValue')}
                    className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    placeholder="0.00"
                    disabled={isViewMode}
                  />
                </div>
                {errors.insuredValue && <p className="text-red-500 text-xs mt-1">{errors.insuredValue}</p>}
              </div>

              <div>
                <label htmlFor="noClaimsBonus" className="block text-gray-700 font-bold mb-2 text-sm">
                  No Claims Bonus
                </label>
                <select
                  id="noClaimsBonus"
                  name="noClaimsBonus"
                  value={formData.noClaimsBonus}
                  onChange={handleChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  disabled={isViewMode}
                >
                  <option value="">Select no claims bonus</option>
                  <option value="12 Months  (1 Year)">12 Months  (1 Year)</option>
                  <option value="24 Months  (2 Year)">24 Months  (2 Year)</option>
                  <option value="36 Months  (3 Year)">36 Months  (3 Year)</option>
                  <option value="48 Months  (4 Year)">48 Months  (4 Year)</option>
                  <option value="60 Months  (5 Year)">60 Months  (5 Year)</option>
                  <option value="72 Months  (6 Year)">72 Months  (6 Year)</option>
                  <option value="84 Months  (7 Year)">84 Months  (7 Year)</option>
                  <option value="96 Months  (8 Year)">96 Months  (8 Year)</option>
                  <option value="108 Months  (9 Year)">108 Months  (9 Year)</option>
                  <option value="120Months  (10 Year)">120 Months  (10 Year)</option>
                </select>
                {errors.noClaimsBonus && <p className="text-red-500 text-xs mt-1">{errors.noClaimsBonus}</p>}
              </div>
            
              <div>
                <label htmlFor="owner" className="block text-gray-700 font-bold mb-2 text-sm">
                  Owner
                </label>
                <select
                  id="owner"
                  name="owner"
                  value={formData.owner}
                  onChange={handleChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  disabled={isLoadingClients || isViewMode}
                >
                  <option value="" disabled>Select an owner</option>
                  {isLoadingClients ? (
                    <option value="" disabled>Loading clients...</option>
                  ) : clients.length === 0 ? (
                    <option value="" disabled>No clients available</option>
                  ) : (
                    clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))
                  )}
                </select>
                {errors.owner && <p className="text-red-500 text-xs mt-1">{errors.owner}</p>}
                {isLoadingClients && <p className="text-xs text-gray-500 mt-1">Loading clients...</p>}
              </div>
            </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="useRetailValue"
                    name="useRetailValue"
                    checked={formData.useRetailValue}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-500 focus:ring-blue-300 border-gray-300 rounded"
                    disabled={isViewMode}
                  />
                  <label htmlFor="useRetailValue" className="ml-2 text-gray-700 text-sm">
                    Use retail value?
                  </label>
                </div>

                <div>
                  <label htmlFor="premium" className="block text-gray-700 font-bold mb-2 text-sm">
                    Premium
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">R</span>
                    </div>
                    <input
                      type="text"
                      id="premium"
                      name="premium"
                      value={currencyDisplay.premium}
                      onChange={handleChange}
                      onBlur={() => handleCurrencyBlur('premium')}
                      className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      placeholder="0.00"
                      disabled={isViewMode}
                    />
                  </div>
                  {errors.premium && <p className="text-red-500 text-xs mt-1">{errors.premium}</p>}
                </div>

                <div>
                  <label htmlFor="excess" className="block text-gray-700 font-bold mb-2 text-sm">
                    Excess <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">R</span>
                    </div>
                    <input
                      type="text"
                      id="excess"
                      name="excess"
                      value={currencyDisplay.excess}
                      onChange={handleChange}
                      onBlur={() => handleCurrencyBlur('excess')}
                      className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      placeholder="0.00"
                      disabled={isViewMode}
                    />
                  </div>
                  {errors.excess && <p className="text-red-500 text-xs mt-1">{errors.excess}</p>}
                </div>
              </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleClose}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </button>
            {!isViewMode && (
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm disabled:bg-blue-300"
                disabled={localSaveStatus === 'loading' || localSaveStatus === 'success'}
              >
                {localSaveStatus === 'loading' ? 'Saving...' : 
                 localSaveStatus === 'success' ? 'Saved!' :
                 isEditMode ? 'Update Trailer/Caravan' : 'Submit Trailer/Caravan'}
              </button>
            )}
          </div>

        </form>
      </div>
    </div>
  );
};

// Editable Card Component
export const EditableTrailerCaravanCard: React.FC<EditableTrailerCaravanCardProps> = ({
  trailerCaravan,
  index,
  onSave,
  onCancel,
  onEdit,
  cardNumber,
  cardTitle = 'Trailer/Caravan',
  showCardNumber = true,
  clientId
}) => {

  // Add this helper function inside the EditableTrailerCaravanCard component
  const ensureString = (value: string | number | undefined): string => {
    if (value === undefined || value === null) return '';
    return typeof value === 'number' ? value.toString() : String(value);
  };

  const [formData, setFormData] = useState(() => {
    console.log('📦 Initializing card form data:', trailerCaravan);
    return {
      vehicleType: trailerCaravan.vehicleType || trailerCaravan.vehicle_type || 'Trailer',
      registrationNumber: trailerCaravan.registrationNumber || trailerCaravan.registration_number || '',
      make: trailerCaravan.make || '',
      model: trailerCaravan.model || '',
      year: trailerCaravan.year || '',
      coverType: trailerCaravan.coverType || trailerCaravan.cover_type || '',
      insuredValue: trailerCaravan.insuredValue || trailerCaravan.insured_value || '',
      owner: trailerCaravan.owner || '',
      useRetailValue: trailerCaravan.useRetailValue !== undefined ? trailerCaravan.useRetailValue : (trailerCaravan.use_retail_value !== undefined ? trailerCaravan.use_retail_value : false),
      premium: trailerCaravan.premium || '',
      excess: trailerCaravan.excess || '',
      noClaimsBonus: trailerCaravan.noClaimsBonus || trailerCaravan.no_claims_bonus || '',
    };
  });

  const [currencyDisplay, setCurrencyDisplay] = useState(() => {
    const insuredValue = trailerCaravan.insuredValue || trailerCaravan.insured_value || '';
    const premium = trailerCaravan.premium || '';
    const excess = trailerCaravan.excess || '';
    
    // Convert to numbers if needed
    const insuredNum = insuredValue ? 
      (typeof insuredValue === 'string' ? parseFloat(insuredValue) : Number(insuredValue)) : 0;
    const premiumNum = premium ? 
      (typeof premium === 'string' ? parseFloat(premium) : Number(premium)) : 0;
    const excessNum = excess ? 
      (typeof excess === 'string' ? parseFloat(excess) : Number(excess)) : 0;
    
    return {
      insuredValue: insuredNum > 0 ? formatCurrencyForDisplay(insuredNum) : '',
      premium: premiumNum > 0 ? formatCurrencyForDisplay(premiumNum) : '',
      excess: excessNum > 0 ? formatCurrencyForDisplay(excessNum) : '',
    };
  });

  const [errors, setErrors] = useState({
    vehicleType: '',
    registrationNumber: '',
    make: '',
    model: '',
    year: '',
    coverType: '',
    insuredValue: '',
    owner: '',
    premium: '',
    excess: '',
    noClaimsBonus: '',
  });

  const [clients, setClients] = useState<Client[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  useEffect(() => {
    const fetchClientsAndPOIs = async () => {
      setIsLoadingClients(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        // If clientId is provided, fetch current client and POIs
        if (clientId) {
          // 1. Fetch current client details
          const clientResponse = await fetch(`${API_URL}/api/clients/${clientId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (!clientResponse.ok) {
            throw new Error(`Failed to fetch current client: ${clientResponse.status}`);
          }

          const clientData = await clientResponse.json();
          const currentClient = {
            id: clientData.id?.toString() || clientId.toString(),
            name: clientData.name || `${clientData.first_name || ''} ${clientData.last_name || ''}`.trim() || `Client ${clientId}`,
            isCurrentClient: true
          };

          // 2. Fetch persons of interest for this client USING QUERY PARAMETER
          let personsOfInterest: any[] = [];
          try {
            const poiResponse = await fetch(`${API_URL}/api/persons-of-interest?clientId=${clientId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });

            if (poiResponse.ok) {
              const poiData = await poiResponse.json();
              console.log('Persons of interest data (card):', poiData);
              
              personsOfInterest = (poiData.data || poiData || []).map((poi: any) => ({
                id: `poi-${poi.id}`,
                // Combine name and surname if both exist
                name: `${poi.name || ''} ${poi.surname || ''}`.trim() || poi.name || poi.surname || `Person of Interest ${poi.id}`,
              })).filter((poi: any) => poi.id && poi.name);
            }
          } catch (poiError) {
            console.warn('Could not fetch persons of interest:', poiError);
            // Continue without POIs if this fails
          }

          // 3. Combine current client and persons of interest
          const allOwners: Client[] = [
            currentClient,
            ...personsOfInterest
          ].filter(item => item.id && item.name);

          setClients(allOwners);
        } else {
          // If no clientId, fetch all clients (fallback)
          const response = await fetch(`${API_URL}/api/clients`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error(`Server error: ${response.status}`);
          }

          const data = await response.json();
          const formattedClients = (data.data || data || []).map((client: any) => ({
            id: client.id?.toString(),
            name: client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim(),
            isCurrentClient: false
          })).filter((client: Client) => client.id && client.name);

          setClients(formattedClients);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
        setClients([]);
      } finally {
        setIsLoadingClients(false);
      }
    };

    fetchClientsAndPOIs();
  }, [clientId]); // Add clientId as dependency

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    
    if (name === 'insuredValue' || name === 'premium' || name === 'excess') {
      // Treat value as string since it comes from an input
      const stringValue = value; // This is always a string from HTML input
      const formattedValue = formatCurrencyInput(stringValue);
      const numericValue = parseCurrencyInput(stringValue);
      
      setFormData(prev => ({
        ...prev,
        [name]: numericValue || numericValue === 0 ? numericValue : '',
      }));
      
      setCurrencyDisplay(prev => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
    
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleCurrencyBlur = (field: 'insuredValue' | 'premium' | 'excess') => {
    const currentValue = currencyDisplay[field];
    if (currentValue) {
      const numericValue = parseCurrencyInput(currentValue);
      if (numericValue > 0) {
        const formatted = formatCurrencyInput(numericValue.toFixed(2));
        setCurrencyDisplay(prev => ({ ...prev, [field]: formatted }));
      }
    }
  };

  const validateForm = () => {
    const currentYear = new Date().getFullYear();
    const minYear = 1900;
    const maxYear = currentYear + 1;
    
    let yearError = '';
    if (!formData.year) {
      yearError = 'Year is required';
    } else {
      const yearNum = parseInt(formData.year);
      if (isNaN(yearNum) || yearNum < minYear || yearNum > maxYear) {
        yearError = `Year must be between ${minYear} and ${maxYear}`;
      }
    }

    const newErrors = {
      vehicleType: !formData.vehicleType ? 'Vehicle Type is required' : '',
      registrationNumber: !formData.registrationNumber ? 'Registration Number is required' : '',
      make: !formData.make ? 'Make is required' : '',
      model: !formData.model ? 'Model is required' : '',
      year: yearError,
      coverType: !formData.coverType ? 'Cover Type is required' : '',
      insuredValue: !formData.insuredValue ? 'Insured Value is required' : '',
      owner: !formData.owner ? 'Owner is required' : '',
      premium: '',
      excess: '',
      noClaimsBonus: !formData.noClaimsBonus ? 'No Claims Bonus is required' : '',
    };
    
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSave = () => {
  if (!validateForm()) return;
  
  // Use camelCase field names for backend
  const currentYear = new Date().getFullYear();
  const minYear = 1900;
  
  // Handle year conversion safely - FIXED: Use String() constructor
  let yearValue: string | undefined = undefined;
  if (formData.year) {
    // Convert to string using String() constructor - safer than .toString()
    const yearStr = String(formData.year);
    
    const parsed = parseInt(yearStr, 10);
    if (!isNaN(parsed)) {
      const clamped = Math.max(minYear, Math.min(currentYear + 1, parsed));
      yearValue = String(clamped); // Use String() instead of .toString()
    }
  }

  const updatedData = {
    vehicleType: formData.vehicleType,
    registrationNumber: formData.registrationNumber,
    make: formData.make,
    model: formData.model,
    year: yearValue,
    coverType: formData.coverType,
    insuredValue: formData.insuredValue,
    use_retail_value: formData.useRetailValue,
    premium: formData.premium,
    excess: formData.excess,
    noClaimsBonus: formData.noClaimsBonus,
    id: trailerCaravan.id,
    isEditing: false
  };
    
  onSave(index, updatedData);
};

  const handleCancel = () => {
    onCancel(index);
  };

  // If not in editing mode, show the display card
  if (!trailerCaravan.isEditing) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="p-4">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-semibold text-orange-600">
              {showCardNumber ? `${cardTitle} #${cardNumber || (index + 1)}` : cardTitle}
            </h4>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onEdit(index)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Edit
              </button>
            </div>
          </div>
          
          <div className="space-y-2 text-sm">
            {/* Make and Model */}
            <div className="flex justify-between">
              <span className="text-gray-600">Make/Model:</span>
              <span className="font-medium text-right max-w-[150px]">
                {trailerCaravan.make} {trailerCaravan.model}
              </span>
            </div>
            
            {/* Year Model */}
            {trailerCaravan.year && (
              <div className="flex justify-between">
                <span className="text-gray-600">Year Model:</span>
                <span className="font-medium">{trailerCaravan.year}</span>
              </div>
            )}
            
            {/* Registration Number */}
            {trailerCaravan.registrationNumber && (
              <div className="flex justify-between">
                <span className="text-gray-600">Registration Number:</span>
                <span className="font-medium">{trailerCaravan.registrationNumber}</span>
              </div>
            )}
            
            <div className="flex justify-between">
              <span className="text-gray-600">Sum Insured:</span>
              <span className="font-medium text-green-600">
                {trailerCaravan.insuredValue ? `R ${formatCurrencyForDisplay(trailerCaravan.insuredValue)}` : 'Not set'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Premium:</span>
              <span className="font-medium text-purple-600">
                {trailerCaravan.premium ? `R ${formatCurrencyForDisplay(trailerCaravan.premium)}` : 'Not set'}
              </span>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // Editing mode - show the form
  return (
    <div className="bg-white border-2 border-blue-200 rounded-lg shadow-md p-4 mb-4">
      <h4 className="font-bold text-lg mb-4 text-blue-700">Edit Trailer/Caravan</h4>
      
      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor={`vehicleType-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Vehicle Type
              </label>
              <select
                id={`vehicleType-${index}`}
                name="vehicleType"
                value={formData.vehicleType}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              >
                <option value="Trailer">Trailer</option>
                <option value="Caravan">Caravan</option>
              </select>
              {errors.vehicleType && <p className="text-red-500 text-xs mt-1">{errors.vehicleType}</p>}
            </div>
            
            <div>
              <label htmlFor={`registrationNumber-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Registration Number
              </label>
              <input
                type="text"
                id={`registrationNumber-${index}`}
                name="registrationNumber"
                value={formData.registrationNumber}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                placeholder="Enter registration number"
              />
              {errors.registrationNumber && <p className="text-red-500 text-xs mt-1">{errors.registrationNumber}</p>}
            </div>
            
            <div>
              <label htmlFor={`make-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Trailer/Caravan Make
              </label>
              <input
                type="text"
                id={`make-${index}`}
                name="make"
                value={formData.make}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                placeholder="Enter make"
              />
              {errors.make && <p className="text-red-500 text-xs mt-1">{errors.make}</p>}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor={`model-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Trailer/Caravan Model
              </label>
              <input
                type="text"
                id={`model-${index}`}
                name="model"
                value={formData.model}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                placeholder="Enter model"
              />
              {errors.model && <p className="text-red-500 text-xs mt-1">{errors.model}</p>}
            </div>
            
            <div>
              <label htmlFor={`year-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Year
              </label>
              <input
                type="number"
                id={`year-${index}`}
                name="year"
                value={formData.year}
                onChange={handleChange}
                min="1900"
                max={new Date().getFullYear() + 1}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder={`Enter year (1900-${new Date().getFullYear() + 1})`}
              />
              {errors.year && <p className="text-red-500 text-xs mt-1">{errors.year}</p>}
            </div>
            
            <div>
              <label htmlFor={`coverType-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Cover Type
              </label>
              <select
                id={`coverType-${index}`}
                name="coverType"
                value={formData.coverType}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              >
                <option value="">Select cover type</option>
                <option value="Comprehensive">Comprehensive</option>
                <option value="Third Party">Compulsary Third Party</option>
                <option value="Fire and Theft">Third Party Fire and Theft</option>
                <option value="Property Damage">Third Party Property Damage</option>
              </select>
              {errors.coverType && <p className="text-red-500 text-xs mt-1">{errors.coverType}</p>}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor={`insuredValue-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Insured Value
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">R</span>
                </div>
                <input
                  type="text"
                  id={`insuredValue-${index}`}
                  name="insuredValue"
                  value={currencyDisplay.insuredValue}
                  onChange={handleChange}
                  onBlur={() => handleCurrencyBlur('insuredValue')}
                  className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="0.00"
                />
              </div>
              {errors.insuredValue && <p className="text-red-500 text-xs mt-1">{errors.insuredValue}</p>}
            </div>

            <div>
              <label htmlFor={`noClaimsBonus-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                No Claims Bonus
              </label>
              <select
                id={`noClaimsBonus-${index}`}
                name="noClaimsBonus"
                value={formData.noClaimsBonus}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              >
                <option value="">Select no claims bonus</option>
                <option value="12 Months  (1 Year)">12 Months  (1 Year)</option>
                  <option value="24 Months  (2 Year)">24 Months  (2 Year)</option>
                  <option value="36 Months  (3 Year)">36 Months  (3 Year)</option>
                  <option value="48 Months  (4 Year)">48 Months  (4 Year)</option>
                  <option value="60 Months  (5 Year)">60 Months  (5 Year)</option>
                  <option value="72 Months  (6 Year)">72 Months  (6 Year)</option>
                  <option value="84 Months  (7 Year)">84 Months  (7 Year)</option>
                  <option value="96 Months  (8 Year)">96 Months  (8 Year)</option>
                  <option value="108 Months  (9 Year)">108 Months  (9 Year)</option>
                  <option value="120Months  (10 Year)">120 Months  (10 Year)</option>
              </select>
              {errors.noClaimsBonus && <p className="text-red-500 text-xs mt-1">{errors.noClaimsBonus}</p>}
            </div>

            <div>
              <label htmlFor={`owner-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Owner
              </label>
              <select
                id={`owner-${index}`}
                name="owner"
                value={formData.owner}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                disabled={isLoadingClients}
              >
                <option value="" disabled>Select an owner</option>
                {isLoadingClients ? (
                  <option value="" disabled>Loading clients...</option>
                ) : clients.length === 0 ? (
                  <option value="" disabled>No clients available</option>
                ) : (
                  clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name} {client.isCurrentClient ? '(Current Client)' : ''}
                    </option>
                  ))
                )}
              </select>
              {errors.owner && <p className="text-red-500 text-xs mt-1">{errors.owner}</p>}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id={`useRetailValue-${index}`}
                name="useRetailValue"
                checked={formData.useRetailValue}
                onChange={handleChange}
                className="h-4 w-4 text-blue-500 focus:ring-blue-300 border-gray-300 rounded"
              />
              <label htmlFor={`useRetailValue-${index}`} className="ml-2 text-gray-700 text-sm">
                Use retail value?
              </label>
            </div>

            <div>
              <label htmlFor={`premium-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Premium
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">R</span>
                </div>
                <input
                  type="text"
                  id={`premium-${index}`}
                  name="premium"
                  value={currencyDisplay.premium}
                  onChange={handleChange}
                  onBlur={() => handleCurrencyBlur('premium')}
                  className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="0.00"
                />
              </div>
              {errors.premium && <p className="text-red-500 text-xs mt-1">{errors.premium}</p>}
            </div>

            <div>
              <label htmlFor={`excess-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Excess
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">R</span>
                </div>
                <input
                  type="text"
                  id={`excess-${index}`}
                  name="excess"
                  value={currencyDisplay.excess}
                  onChange={handleChange}
                  onBlur={() => handleCurrencyBlur('excess')}
                  className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="0.00"
                />
              </div>
              {errors.excess && <p className="text-red-500 text-xs mt-1">{errors.excess}</p>}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleCancel}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm"
          >
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
  return null;
};

export default TrailerCaravanModal;